import { useEffect, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Stack, MenuItem, Alert } from '@mui/material';
import type { LabourRate, CreateLabourRateInput } from '../../api/labourRates.api';

interface Props {
  open: boolean;
  initial?: LabourRate | null;
  onClose: () => void;
  onSubmit: (input: CreateLabourRateInput) => Promise<void>;
  error?: string | null;
}

const empty: CreateLabourRateInput = { activityName: '', unit: '', standardRate: 0, status: 'active' };

export function LabourRateFormDialog({ open, initial, onClose, onSubmit, error }: Props) {
  const isEdit = Boolean(initial);
  const [form, setForm] = useState<CreateLabourRateInput>(empty);

  useEffect(() => {
    setForm(
      initial
        ? { activityName: initial.activityName, unit: initial.unit, standardRate: initial.standardRate, status: initial.status }
        : empty,
    );
  }, [initial, open]);

  const set = <K extends keyof CreateLabourRateInput>(k: K, v: CreateLabourRateInput[K]) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{isEdit ? `Edit Labour Rate — ${initial?.activityCode}` : 'Create Labour Rate'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt={1}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField label="Activity Name" value={form.activityName} onChange={(e) => set('activityName', e.target.value)} required fullWidth />
          <Stack direction="row" spacing={2}>
            <TextField label="Unit" value={form.unit} onChange={(e) => set('unit', e.target.value)} required fullWidth />
            <TextField label="Standard Rate" type="number" value={form.standardRate} onChange={(e) => set('standardRate', Number(e.target.value))} required fullWidth />
          </Stack>
          <TextField select label="Status" value={form.status} onChange={(e) => set('status', e.target.value as CreateLabourRateInput['status'])} fullWidth>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="inactive">Inactive</MenuItem>
          </TextField>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={() => onSubmit(form)}>{isEdit ? 'Save' : 'Create'}</Button>
      </DialogActions>
    </Dialog>
  );
}
